import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import {
  ArrowLeft,
  RefreshCw,
  Download,
  Send,
  Calendar,
  User,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  FileText,
  MessageSquare,
  FileDown,
} from 'lucide-react';
import { API_URL } from '../../Config';

export default function DemandeTimeline() {
  const { demandeId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [exporting, setExporting] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTimeline = async () => {
    try {
      setLoading(true);
      setError('');
      
      const res = await axios.get(
        `${API_URL}/historique/demande/${demandeId}/timeline`,
        { headers }
      );

      if (res.data.success) {
        setData(res.data.data);
      } else {
        setError(res.data.message || 'Données non disponibles');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/historique/demande/${demandeId}/comment`,
        { commentaire: comment },
        { headers }
      );
      setComment('');
      fetchTimeline();
    } catch (err) {
      alert('Erreur lors de l\'ajout du commentaire');
    } finally {
      setSending(false);
    }
  };

  // Génération PDF professionnel avec QR code
const exportPDF = async () => {
  if (!data) return;

  setExporting(true);

  try {
    const { demande, timeline } = data;

    // S'assurer que les données de timeline existent
    const safeTimeline = timeline || [];
    
    const allEvents = safeTimeline
      .flatMap(day => {
        if (!day || !day.evenements) return [];
        return day.evenements.map(event => ({
          ...event,
          date: day.date,
          date_complete: day.date_complete || day.date
        }));
      })
      .sort((a, b) => new Date(a.date_complete || a.date) - new Date(b.date_complete || b.date));

    // Générer le QR code avec gestion d'erreur
    let qrCodeUrl = '';
    try {
      qrCodeUrl = await QRCode.toDataURL(
        `${window.location.origin}/demande-timeline/${demande.id}`,
        {
          width: 100,
          margin: 2,
          color: { dark: '#000000', light: '#ffffff' }
        }
      );
    } catch (qrError) {
      console.warn('Erreur génération QR code:', qrError);
      qrCodeUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNmZmZmZmYiLz48dGV4dCB4PSI1MCIgeT0iNTAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMiIgZmlsbD0iIzAwMDAwMCI+UVIgQ29kZTwvdGV4dD48L3N2Zz4=';
    }

    // Fonction pour formater les dates de manière sécurisée
    const formatDate = (dateString, formatStr) => {
      try {
        if (!dateString) return '—';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '—';
        return format(date, formatStr, { locale: fr });
      } catch (error) {
        console.warn('Erreur formatage date:', error);
        return '—';
      }
    };

    // Fonction pour nettoyer le texte HTML
    const escapeHtml = (text) => {
      if (!text) return '';
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    };

    // Préparer les données pour l'affichage
    const demandeNumero = escapeHtml(demande.numero || 'N/A');
    const demandeur = escapeHtml(demande.demandeur || 'Non spécifié');
    const statut = demande.statut 
      ? escapeHtml(demande.statut.replace(/_/g, ' ').toUpperCase())
      : 'INCONNU';

    // Créer un conteneur pour le rendu PDF
    const pdfContainer = document.createElement('div');
    pdfContainer.id = 'pdf-container-' + Date.now();
    pdfContainer.style.position = 'fixed';
    pdfContainer.style.left = '-10000px';
    pdfContainer.style.top = '0';
    pdfContainer.style.width = '794px';
    pdfContainer.style.height = 'auto';
    pdfContainer.style.background = '#ffffff';
    pdfContainer.style.fontFamily = "Helvetica, Arial, sans-serif";
    pdfContainer.style.color = '#1a1a1a';
    pdfContainer.style.lineHeight = '1.5';
    pdfContainer.style.overflow = 'visible';
    pdfContainer.style.zIndex = '9999';

    // Construire le contenu HTML de manière sécurisée
    pdfContainer.innerHTML = `
      <div style="padding: 40px;">
        <!-- En-tête -->
        <div style="margin-bottom: 40px;">
          <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #000000; padding-bottom: 20px;">
            <div>
              <h1 style="font-size: 28px; font-weight: bold; margin: 0 0 8px 0;">
                SUIVI DE DEMANDE
              </h1>
              <p style="font-size: 14px; color: #666666; margin: 0;">
                Document officiel - Système de Gestion de Stock
              </p>
            </div>
            <div style="text-align: right;">
              <p style="font-size: 12px; color: #666666; margin: 0 0 6px 0; font-weight: bold;">
                RÉFÉRENCE
              </p>
              <p style="font-size: 22px; font-weight: bold; margin: 0;">
                ${demandeNumero}
              </p>
            </div>
          </div>
        </div>

        <!-- Informations de base -->
        <div style="margin-bottom: 40px;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 20px;">
            <div style="flex: 1; padding-right: 20px;">
              <p style="font-size: 11px; color: #666666; margin: 0 0 4px 0; font-weight: bold;">
                DEMANDEUR
              </p>
              <p style="font-size: 16px; margin: 0;">
                ${demandeur}
              </p>
            </div>
            <div style="flex: 1;">
              <p style="font-size: 11px; color: #666666; margin: 0 0 4px 0; font-weight: bold;">
                DATE DE CRÉATION
              </p>
              <p style="font-size: 16px; margin: 0;">
                ${formatDate(demande.date_creation, 'dd MMMM yyyy')}
              </p>
            </div>
          </div>
          
          <div style="background: #f5f5f5; border: 1px solid #e0e0e0; padding: 16px; border-radius: 4px;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <div>
                <p style="font-size: 11px; color: #666666; margin: 0 0 4px 0; font-weight: bold;">
                  STATUT ACTUEL
                </p>
                <div style="display: inline-block; padding: 6px 16px; background: #000000; color: #ffffff; font-size: 13px; font-weight: bold; border-radius: 4px;">
                  ${statut}
                </div>
              </div>
              <div style="text-align: right;">
                <p style="font-size: 11px; color: #666666; margin: 0 0 4px 0; font-weight: bold;">
                  DOCUMENT GÉNÉRÉ LE
                </p>
                <p style="font-size: 14px; margin: 0;">
                  ${formatDate(new Date(), 'dd/MM/yyyy à HH:mm')}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Résumé -->
        <div style="margin-bottom: 40px;">
          <h2 style="font-size: 18px; font-weight: bold; margin: 0 0 20px 0; border-bottom: 2px solid #e0e0e0; padding-bottom: 8px;">
            RÉSUMÉ
          </h2>
          
          <div style="display: flex; justify-content: space-between; text-align: center;">
            <div style="flex: 1;">
              <p style="font-size: 24px; font-weight: bold; margin: 0 0 4px 0;">${allEvents.length}</p>
              <p style="font-size: 12px; color: #666666; margin: 0;">événements</p>
            </div>
            <div style="flex: 1; border-left: 1px solid #e0e0e0; border-right: 1px solid #e0e0e0;">
              <p style="font-size: 16px; font-weight: bold; margin: 0 0 4px 0;">
                ${allEvents.length > 0 ? formatDate(allEvents[0].date_complete, 'dd/MM/yy') : '—'}
              </p>
              <p style="font-size: 12px; color: #666666; margin: 0;">premier</p>
            </div>
            <div style="flex: 1;">
              <p style="font-size: 16px; font-weight: bold; margin: 0 0 4px 0;">
                ${allEvents.length > 0 ? formatDate(allEvents[allEvents.length - 1].date_complete, 'dd/MM/yy') : '—'}
              </p>
              <p style="font-size: 12px; color: #666666; margin: 0;">dernier</p>
            </div>
          </div>
        </div>

        <!-- Chronologie -->
        <div style="margin-bottom: 50px;">
          <h2 style="font-size: 18px; font-weight: bold; margin: 0 0 24px 0; border-bottom: 2px solid #e0e0e0; padding-bottom: 8px;">
            CHRONOLOGIE DES ÉVÉNEMENTS
          </h2>

          <div style="position: relative; padding-left: 30px;">
            <div style="position: absolute; left: 11px; top: 8px; bottom: 8px; width: 1px; background: #cccccc;"></div>

            ${allEvents.map((event, index) => {
              const eventType = event.type_action 
                ? escapeHtml(event.type_action.replace(/_/g, ' ').toUpperCase())
                : 'ÉVÉNEMENT';
              
              const userName = event.user?.nom ? escapeHtml(event.user.nom) : '';
              const userRole = event.user?.role ? escapeHtml(event.user.role) : '';
              const description = event.description ? escapeHtml(event.description) : '';
              
              return `
                <div style="position: relative; margin-bottom: 24px;">
                  <div style="position: absolute; left: -30px; top: 6px; width: 12px; height: 12px; background: #ffffff; border: 2px solid #000000; border-radius: 50%;"></div>
                  
                  <div>
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                      <div>
                        <p style="font-size: 14px; font-weight: bold; margin: 0 0 4px 0;">
                          ${eventType}
                        </p>
                        <p style="font-size: 12px; color: #666666; margin: 0;">
                          ${formatDate(event.date_complete, 'dd MMMM yyyy')} ${event.heure ? `à ${escapeHtml(event.heure)}` : ''}
                        </p>
                      </div>
                      ${userName ? `
                        <div style="text-align: right;">
                          ${userRole ? `<p style="font-size: 12px; color: #666666; margin: 0 0 2px 0;">${userRole}</p>` : ''}
                          <p style="font-size: 13px; font-weight: bold; margin: 0;">
                            ${userName}
                          </p>
                        </div>
                      ` : ''}
                    </div>
                    
                    ${description ? `
                      <div style="background: #fafafa; border-left: 3px solid #cccccc; padding: 12px; margin-bottom: 12px; border-radius: 0 4px 4px 0;">
                        <p style="font-size: 14px; margin: 0; line-height: 1.6;">
                          ${description}
                        </p>
                      </div>
                    ` : ''}
                    
                    ${event.materiel ? `
                      <div style="display: flex; align-items: center; gap: 12px; padding: 12px; background: #f9f9f9; border-radius: 4px; border: 1px solid #e0e0e0;">
                        <div style="font-weight: bold; font-size: 12px; color: #666666;">
                          Matériel
                        </div>
                        <div>
                          <p style="font-size: 14px; font-weight: bold; margin: 0 0 2px 0;">
                            ${escapeHtml(event.materiel.nom || '')}
                          </p>
                          <p style="font-size: 12px; color: #666666; margin: 0;">
                            Référence : ${escapeHtml(event.materiel.reference || '')}
                          </p>
                        </div>
                      </div>
                    ` : ''}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>

        <!-- Pied de page -->
        <div style="border-top: 1px solid #e0e0e0; padding-top: 24px; margin-top: 40px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <div>
              <div style="display: flex; align-items: center; gap: 16px;">
                <img src="${qrCodeUrl}" style="width: 80px; height: 80px; border: 1px solid #e0e0e0; background: white;" />
                <div>
                  <p style="font-size: 11px; color: #666666; margin: 0 0 4px 0; font-weight: bold;">
                    CODE DE VÉRIFICATION
                  </p>
                  <p style="font-size: 10px; color: #999999; margin: 0;">
                    Scannez pour accéder au suivi en ligne
                  </p>
                </div>
              </div>
            </div>
            
            <div style="text-align: right;">
              <p style="font-size: 12px; color: #666666; margin: 0 0 4px 0;">
                CR Mansoria • Système de Gestion de Stock
              </p>
              <p style="font-size: 10px; color: #999999; margin: 0;">
                Document confidentiel
              </p>
            </div>
          </div>
        </div>
      </div>
    `;

    // Ajouter le conteneur au DOM
    document.body.appendChild(pdfContainer);

    // Attendre un peu que les images se chargent
    await new Promise(resolve => setTimeout(resolve, 500));

    // Générer le canvas
    const canvas = await html2canvas(pdfContainer, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: 794,
      height: pdfContainer.scrollHeight,
      windowWidth: 794,
      onclone: (clonedDoc, element) => {
        // S'assurer que tout est visible
        element.style.overflow = 'visible';
        element.style.position = 'relative';
      }
    }).catch(error => {
      console.error('Erreur html2canvas:', error);
      throw new Error('Échec de la conversion en image');
    });

    // Nettoyer le conteneur
    document.body.removeChild(pdfContainer);

    // Créer le PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
      putOnlyUsedFonts: true
    });

    // Ajouter les métadonnées
    pdf.setProperties({
      title: `Suivi Demande ${demande.numero}`,
      subject: 'Historique de traitement de demande',
      author: 'Système de Gestion de Stock',
      creator: 'CR Mansoria',
      keywords: 'demande, suivi, historique, stock'
    });

    const imgWidth = 190; // Largeur A4 moins marges (210 - 20)
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const pageHeight = 277; // Hauteur A4 moins marges (297 - 20)

    // Ajouter l'image sur la première page
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 10, imgWidth, imgHeight);

    // Ajouter des pages supplémentaires si nécessaire
    let heightLeft = imgHeight;
    let position = 10;

    while (heightLeft > 0) {
      position = position - pageHeight;
      if (position < -pageHeight) break;
      
      pdf.addPage();
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Nom du fichier
    const fileName = `Suivi_Demande_${demande.numero}_${formatDate(new Date(), 'yyyy-MM-dd')}.pdf`;
    
    // Sauvegarder le PDF
    pdf.save(fileName);

  } catch (error) {
    console.error('Erreur détaillée lors de la génération du PDF:', error);
    alert(`Erreur: ${error.message || 'Impossible de générer le PDF. Veuillez réessayer.'}`);
  } finally {
    setExporting(false);
  }
};

  useEffect(() => {
    fetchTimeline();
  }, [demandeId]);

  const getStatusInfo = (status) => {
    const map = {
      en_attente: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'En attente' },
      validee: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', label: 'Validée' },
      refusee: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Refusée' },
      annulee: { icon: AlertCircle, color: 'text-gray-600', bg: '', label: 'Annulée' },
      pret_recuperation: { icon: Package, color: 'text-blue-600', bg: 'bg-blue-50', label: 'Prêt à récupérer' },
      en_preparation: { icon: Package, color: 'text-indigo-600', bg: 'bg-indigo-50', label: 'En préparation' },
    };
    return map[status] || { icon: Clock, color: 'text-gray-500', bg: '', label: status || 'Inconnu' };
  };

  const getActionIcon = (type) => {
    const icons = {
      creation_demande: Package,
      validation: CheckCircle,
      refus: XCircle,
      preparation: Package,
      sortie_stock: Package,
      recuperation: Package,
      modification_quantite: Edit,
      annulation: AlertCircle,
      commentaire: MessageSquare,
    };
    return icons[type] || FileText;
  };

  const formatChatTime = (dateString) => {
    try {
      return format(new Date(dateString), 'HH:mm', { locale: fr });
    } catch {
      return '--:--';
    }
  };

  const formatChatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: fr });
    } catch {
      return 'Date inconnue';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-3"></div>
          <p className="text-gray-600">Chargement du suivi...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen  flex items-center justify-center p-4">
        <div className="text-center bg-white p-8 rounded-xl shadow-sm border border-gray-200 max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-800 mb-2">
            {error || 'Demande introuvable'}
          </p>
          <p className="text-gray-600 mb-6">ID: {demandeId}</p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              Retour
            </button>
            <button
              onClick={() => navigate('/mes-demandes')}
              className="flex-1 px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
            >
              Mes demandes
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { demande, timeline } = data;
  const statusInfo = getStatusInfo(demande.statut);
  const StatusIcon = statusInfo.icon;

  // Aplatir tous les événements et les trier par date
  const allEvents = timeline.flatMap(day => 
    day.evenements.map(event => ({
      ...event,
      date: day.date,
      date_complete: day.date_complete
    }))
  ).sort((a, b) => new Date(a.date_complete) - new Date(b.date_complete));

  return (
    <div className="min-h-screen ">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  Suivi de la demande #{demande.numero}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}>
                    <StatusIcon className="w-3 h-3" />
                    {statusInfo.label}
                  </div>
                  <span className="text-sm text-gray-500">
                    {demande.demandeur} • {formatChatDate(demande.date_creation)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={fetchTimeline}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Actualiser"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={exportPDF}
                disabled={exporting}
                className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors text-sm"
                title="Exporter PDF"
              >
                {exporting ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Génération...
                  </>
                ) : (
                  <>
                    <FileDown className="w-4 h-4" />
                    PDF Pro
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Zone de chat */}
        <div className="px-4 py-6 space-y-6">
          {allEvents.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucun événement enregistré</p>
            </div>
          ) : (
            allEvents.map((event) => {
              const Icon = getActionIcon(event.type_action);
              const isComment = event.type_action === 'commentaire';
              const isSystem = event.user?.role === 'system' || !event.user;
              
              return (
                <div key={event.id} className="flex gap-3">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isSystem ? 'bg-gray-100' : 'bg-blue-100'
                    }`}>
                      <Icon className={`w-4 h-4 ${
                        isSystem ? 'text-gray-500' : 'text-blue-600'
                      }`} />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className={`text-sm font-medium ${
                        isSystem ? 'text-gray-700' : 'text-blue-700'
                      }`}>
                        {event.user?.nom || 'Système'}
                        {event.user?.role && !isSystem && (
                          <span className="text-gray-500 text-xs ml-2">
                            ({event.user.role})
                          </span>
                        )}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatChatTime(event.date_complete)}
                      </span>
                    </div>

                    <div className={`rounded-lg p-3 ${
                      isComment 
                        ? 'bg-blue-50 border border-blue-100' 
                        : isSystem
                        ? ' border border-gray-100'
                        : 'bg-white border border-gray-200'
                    }`}>
                      <p className="text-sm text-gray-800">
                        {event.description}
                      </p>

                      {/* Détails supplémentaires */}
                      {(event.details || event.materiel) && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          {event.details && Object.keys(event.details).length > 0 && (
                            <div className="text-xs text-gray-600 space-y-1 mb-2">
                              {Object.entries(event.details).slice(0, 3).map(([key, value]) => (
                                <div key={key} className="flex">
                                  <span className="font-medium min-w-[80px]">
                                    {key.replace(/_/g, ' ')}:
                                  </span>
                                  <span className="ml-2">{String(value)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {event.materiel && (
                            <div className="flex items-center gap-2 text-xs text-blue-600">
                              <Package className="w-3 h-3" />
                              <span>{event.materiel.nom}</span>
                              <span className="text-gray-500">({event.materiel.reference})</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Zone d'envoi de commentaire */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && addComment()}
              placeholder="Écrivez un commentaire..."
              className="flex-1 px-4 py-2.5  border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm"
            />
            <button
              onClick={addComment}
              disabled={sending || !comment.trim()}
              className="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              {sending ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}